/**
 * S2b · "Same face?" — Lane 2 only (repeat buyer).
 *
 * WHAT REUSE ACTUALLY DOES: it stages the SELFIE already on file and goes to S4,
 * which calls `attach_selfie_to_user` (a fresh selfie-bound biometric consent
 * row) and then `generate-avatar` — a new model run on a face. Reuse is NOT
 * compositing a previously approved avatar, so the age affirmation and the
 * consent both legitimately fire here. Do not "optimise" the tick away on the
 * strength of the word reuse; if this path ever becomes a true avatar
 * re-composite (no model run on a face), revisit the gate, not the copy.
 *
 * LEGAL GATES (L1): no scheme name, no patent claim, no issuance claim.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { readStylePick, saveSelfiePath, readSelfiePath } from "@/lib/consumer-session";
import { AgeAffirmationCard, AgeRefusal, useAgeAffirmation } from "@/components/consumer/AgeAffirmation";
import { useRepeatProfile } from "@/lib/repeat-flow";
import { findTile, tileColor } from "@/lib/style-art";
import { useConsumerFlow } from "../ConsumerFlow";
import {
  CardFace,
  Display,
  FunnelContainer,
  FunnelHeader,
  FunnelPage,
  Lede,
  PrimaryButton,
  StickyBar,
} from "@/components/consumer/chrome";
import { useSiteContent } from "@/hooks/useSiteContent";
import { S2B_SECTION, S2B_CONTENT, S2B_DEFAULTS } from "@/content/screens/s2b";

export default function S2bSameFace() {
  const { program } = useConsumerFlow();
  const navigate = useNavigate();
  const repeat = useRepeatProfile(program.slug);
  const { t } = useSiteContent(S2B_SECTION, S2B_DEFAULTS);
  /**
   * The option tap SELECTS; it never proceeds. Proceeding is always the single
   * primary Continue below, enabled the moment a selection exists — the same
   * pattern as S2 (selfie present ⇒ Continue is the only primary CTA).
   */
  const [choice, setChoice] = useState<"reuse" | "retake" | null>(null);

  const pick = readStylePick(program.slug);
  /** Profile-bound template, or the selfie already staged in this funnel run. */
  const faceOnFile = repeat.storedSelfiePath || readSelfiePath(program.slug);
  const styleMeta = findTile(program.styles, pick?.style_id);
  const styleName = styleMeta?.name || pick?.style_id || t("s2b.style.fallbackName");
  const styleColorValue = tileColor(program.styles, pick?.style_id);
  /** N5 · the allocated gallery template for this style (same source as S1). */
  const galleryPreview = program.styles.find((s) => s.style_id === pick?.style_id)?.preview_image_url ?? null;

  // Lane 1 users, or a repeat user whose stored selfie is gone, never see this
  // fork — they take the normal selfie step.
  /**
   * AGE AFFIRMATION — the repeat lane cannot skip it. Reuse skips S3 entirely and
   * Continue goes straight to generating, which is exactly the thing being gated,
   * so the tick is taken here. It is recorded server-side on the user scope and
   * re-checked by `generate-avatar`.
   */
  const age = useAgeAffirmation(program.slug, program.program_id);
  const ageOk = age.status === "passed";

  useEffect(() => {
    if (repeat.loading) return;
    if (!pick?.style_id) {
      navigate("../style", { replace: true });
      return;
    }
    // Gate on IDENTITY + a face on file, never on the reorder flag: a
    // first-order buyer who verified at S3 and walked back to S1 belongs here.
    if (!repeat.isIdentified || !faceOnFile) {
      navigate("../selfie", { replace: true });
    }
  }, [repeat.loading, repeat.isIdentified, faceOnFile, pick?.style_id, navigate]);

  function proceed() {
    if (!ageOk) return;
    if (choice === "retake") {
      navigate("../selfie");
      return;
    }
    if (choice !== "reuse" || !faceOnFile) return;
    // S4 reads the staged path; point it at the face already on file.
    saveSelfiePath(program.slug, faceOnFile);
    navigate("../generating");
  }

  if (age.status === "blocked") return <AgeRefusal />;

  return (
    <FunnelPage testId="s2b-same-face">
      <FunnelContainer size="form">
        <FunnelHeader backTo="../style" backLabel={t("s2b.header.backLabel")} step={t("s2b.header.step")} />

        <div className="mb-5 flex items-center gap-3 rounded-[14px] border border-foreground/10 bg-white p-[10px] pr-[14px]">
          {/* B4/N5 · the tile shows the SAME gallery template the style tile shows,
              so the allocated preview wins over the (null) local art registry. */}
          <CardFace
            color={styleColorValue}
            art={galleryPreview ?? styleMeta?.art ?? null}
            alt={styleName}
            className="w-[38px] flex-none rounded-[6px]"
          />
          <div className="flex min-w-0 flex-col gap-px">
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-muted">{t("s2b.style.label")}</span>
            <span className="text-[15px] font-semibold tracking-[-0.01em]">{styleName}</span>
          </div>
        </div>

        <Display className="mb-3 max-w-[16ch]">{t("s2b.header.title")}</Display>
        <Lede className="mb-7 max-w-[44ch]">
          {t("s2b.header.subtitle")}
        </Lede>

        {/* N7 · the two choices carry their label only — no explanatory copy. */}
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => setChoice("reuse")}
            data-testid="s2b-reuse"
            aria-pressed={choice === "reuse"}
            className={
              "flex items-center gap-3.5 rounded-[20px] bg-white p-5 text-left transition-transform hover:-translate-y-0.5 " +
              (choice === "reuse" ? "border-2 border-grape" : "border border-foreground/[0.16] hover:border-ink")
            }
          >
            {/* N6 · NEUTRAL icon. A green tick read as "already chosen". */}
            <svg viewBox="0 0 22 22" className="h-5 w-5 flex-none" aria-hidden>
              <rect x="2.6" y="4.4" width="16.8" height="13.2" rx="2.4" fill="none" stroke="#1C2030" strokeWidth="1.8" />
              <circle cx="8.4" cy="9.4" r="1.9" fill="none" stroke="#1C2030" strokeWidth="1.6" />
              <path d="M4.4 15.6l3.6-3.2 2.6 2.2 3-2.6 4 3.6" fill="none" stroke="#1C2030" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-[17px] font-semibold tracking-[-0.01em]">{t("s2b.reuse.title")}</span>
          </button>

          <button
            type="button"
            onClick={() => setChoice("retake")}
            data-testid="s2b-retake"
            aria-pressed={choice === "retake"}
            className={
              "flex items-center gap-3.5 rounded-[20px] bg-white p-5 text-left transition-colors " +
              (choice === "retake" ? "border-2 border-grape" : "border border-foreground/[0.16] hover:border-ink")
            }
          >
            <svg viewBox="0 0 22 22" className="h-5 w-5 flex-none" aria-hidden>
              <path d="M2.6 7.4h3.1l1.5-2.4h7.6l1.5 2.4h3.1v10.2H2.6z" fill="none" stroke="#1C2030" strokeWidth="1.8" strokeLinejoin="round" />
              <circle cx="11" cy="12.3" r="3.4" fill="none" stroke="#1C2030" strokeWidth="1.8" />
            </svg>
            <span className="text-[17px] font-semibold tracking-[-0.01em]">{t("s2b.retake.title")}</span>
          </button>
        </div>

        {/*
          N7 · the shield panel is GONE. Its copy claimed the photo stays on file,
          which contradicts S4. No replacement retention copy is written here: the
          only thing on this screen is a link to the policy that owns the answer.
        */}
        <p className="mt-4 text-[12px] leading-[1.5] text-foreground/60">
          <a href="#" className="text-azure underline hover:text-tangerine" data-testid="s2b-policy-link">
            {t("s2b.legal.link")}
          </a>
        </p>

        {/* N7 · the affirmation sits directly above the CTA. */}
        <div className="mt-6">
          <AgeAffirmationCard
            affirmed={ageOk}
            threshold={age.threshold}
            busy={age.busy}
            error={age.error}
            onAffirm={age.affirm}
            onDecline={age.decline}
            hideHint
          />
        </div>
      </FunnelContainer>

      {/* Selection ⇒ Continue. Never overload the option tap as "proceed". */}
      <StickyBar data-cta-state={choice ? "ready" : "empty"}>
        <PrimaryButton
          disabled={!ageOk || !choice}
          onClick={proceed}
          data-testid="s2b-continue"
          className="basis-full"
        >
          Continue
        </PrimaryButton>
      </StickyBar>
    </FunnelPage>
  );
}

